import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@how/database";

export type HowSupabaseClient = SupabaseClient<Database>;

/** Browser client. Uses the publishable key and the implicit flow (below); sessions persist per origin. */
export function createBrowserClient(url: string, publishableKey: string): HowSupabaseClient {
  return createClient<Database>(url, publishableKey, {
    auth: {
      // Implicit flow on purpose: invitation and magic-link emails are generated by the server (invite-member, the
      // super-admin bootstrap, the sign-in page's "Email me a sign-in link") and must open in any browser. Under the
      // PKCE flow the library rejects those callbacks ("Not a valid PKCE flow url") and the session is silently lost.
      flowType: "implicit",
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });
}

/**
 * Server-only client for Edge Functions and trusted scripts. Takes the secret (service) key,
 * bypasses RLS, and therefore must never be constructed where a browser could observe it.
 */
export function createServerClient(url: string, secretKey: string): HowSupabaseClient {
  if (typeof (globalThis as { window?: unknown }).window !== "undefined") {
    throw new Error("createServerClient must never run in a browser");
  }
  if (secretKey.length === 0) throw new Error("createServerClient requires a secret key");
  return createClient<Database>(url, secretKey, {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
  });
}

/** Server client that acts as a specific end user by forwarding their access token (RLS applies). */
export function createUserScopedServerClient(
  url: string,
  publishableKey: string,
  accessToken: string,
): HowSupabaseClient {
  if (accessToken.length === 0)
    throw new Error("createUserScopedServerClient requires an access token");
  return createClient<Database>(url, publishableKey, {
    global: { headers: { Authorization: `Bearer ${accessToken}` } },
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
  });
}

/**
 * Server-side client for public, unauthenticated reads (the anon role): tenant resolution by hostname on the public
 * websites. No session, no token; RLS and the anon grants decide what it can see.
 */
export function createPublicServerClient(url: string, publishableKey: string): HowSupabaseClient {
  return createClient<Database>(url, publishableKey, {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
  });
}
